import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Lock, ShieldCheck, Mail, CheckCircle2 } from 'lucide-react';
import { useAuth, formatApiError } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginForm({ title = 'Sign in', subtitle, expectedRole, onSuccess, allowForgot = true }) {
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'forgot' | 'forgot-sent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true); setError(null);
    try {
      if (mode === 'login') {
        if (!email || !password) return;
        const u = await login(email.trim().toLowerCase(), password);
        if (expectedRole && u.role !== expectedRole) {
          setError(expectedRole === 'owner'
            ? 'This account is a driver — use the driver portal at /driver.'
            : 'This account is an owner — use the admin dashboard at /admin.');
          return;
        }
        if (onSuccess) onSuccess(u);
      } else if (mode === 'forgot') {
        if (!email) return;
        await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
        setMode('forgot-sent');
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-[color:var(--mv-navy)] px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
        <div className="w-12 h-12 rounded-full bg-[color:var(--mv-navy)] text-white flex items-center justify-center mx-auto">
          <Lock className="w-5 h-5" />
        </div>
        <h1 className="font-display text-2xl font-extrabold text-center text-[color:var(--mv-navy)] mt-4">
          {mode === 'login' ? title : 'Reset your password'}
        </h1>
        {mode === 'login' && subtitle && <p className="text-sm text-slate-500 text-center mt-1">{subtitle}</p>}
        {mode === 'forgot' && <p className="text-sm text-slate-500 text-center mt-1">Enter your email and we'll send a reset link.</p>}

        {mode === 'forgot-sent' ? (
          <div className="mt-5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 flex items-start gap-2" data-testid="forgot-sent">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              If that email exists, a reset link is on its way. Check your inbox (and spam) — the link lasts 1 hour.
              <button type="button" onClick={() => { setMode('login'); setError(null); }} className="block mt-2 text-xs font-bold text-[color:var(--mv-navy)] hover:text-[color:var(--mv-orange)]">← Back to sign in</button>
            </div>
          </div>
        ) : (
          <>
            <div className="relative mt-5">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="login-email"
                autoFocus
                autoComplete="email"
                className="h-11 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm text-[color:var(--mv-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]"
              />
            </div>
            {mode === 'login' && (
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="login-password"
                autoComplete="current-password"
                className="mt-3 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-[color:var(--mv-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]"
              />
            )}
            {error && <div className="mt-2 text-xs text-red-600 text-center" data-testid="login-error">{error}</div>}
            <button
              type="submit"
              disabled={loading || !email || (mode === 'login' && !password)}
              data-testid={mode === 'login' ? 'login-submit' : 'forgot-submit'}
              className="mt-4 w-full h-11 rounded-md bg-[color:var(--mv-orange)] hover:bg-orange-600 disabled:opacity-60 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> {mode === 'login' ? 'Signing in…' : 'Sending link…'}</>
                : mode === 'login'
                  ? <><ShieldCheck className="w-4 h-4" /> Sign in</>
                  : 'Send reset link'}
            </button>
            {allowForgot && (
              <div className="mt-3 text-center">
                {mode === 'login' ? (
                  <button type="button" onClick={() => { setMode('forgot'); setError(null); }} className="text-xs font-bold text-[color:var(--mv-navy)] hover:text-[color:var(--mv-orange)]" data-testid="forgot-link">
                    Forgot password?
                  </button>
                ) : (
                  <button type="button" onClick={() => { setMode('login'); setError(null); }} className="text-xs font-bold text-[color:var(--mv-navy)] hover:text-[color:var(--mv-orange)]">
                    ← Back to sign in
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </form>
    </div>
  );
}
